import { useCallback } from "react";

import { Action, Module, Stack } from "types/generated";
import { AccountContext } from "views/AccountWrapper";
import useTypedContext from "hooks/useTypedContext";
import { AccessResult, hasAtLeastOneRequiredAction, NO_ACCESS_MESSAGE } from "utils/access";
import { AccountViewer } from "components/form/graphql/types";
import useHasStackReadAccess from "shared/Stack/access/useHasStackReadAccess";
import useHasModuleReadAccess from "shared/Module/access/useHasModuleReadAccess";

const COMMENT_ACCESS_TYPES = [Action.RunComment];

const hasCommentAccess = (viewer: AccountViewer, spaceId: string) =>
  hasAtLeastOneRequiredAction(COMMENT_ACCESS_TYPES, viewer, spaceId);

/**
 * Hook that returns a function to check if the current user can comment on a run.
 * A user needs write access to comment on a run.
 * @returns {(entity: Stack | Module) => AccessResult} Function that takes a stack or module and returns an AccessResult indicating if user can comment on it
 */
const useCanCommentRun = (): ((entity: Stack | Module) => AccessResult) => {
  const { viewer } = useTypedContext(AccountContext);
  const hasStackReadAccessFn = useHasStackReadAccess();
  const hasModuleReadAccessFn = useHasModuleReadAccess();

  return useCallback(
    (entity: Stack | Module) => {
      const isStack = entity.__typename === "Stack";
      const isModule = entity.__typename === "Module";

      const hasAccessToComment =
        (isStack && hasStackReadAccessFn(entity).result) ||
        (isModule && hasModuleReadAccessFn(entity)) ||
        hasCommentAccess(viewer, entity.spaceDetails.id || entity.space);

      if (hasAccessToComment) {
        return {
          result: true,
        };
      }

      return {
        result: false,
        message: NO_ACCESS_MESSAGE,
      };
    },
    [viewer, hasStackReadAccessFn, hasModuleReadAccessFn]
  );
};

export default useCanCommentRun;
